// The alarm recipe LIBRARY (UI-3 authoring). Fetches `GET /v1/alert-templates`
// — the ~20-recipe registry derived from the shared @neomaris/rules specs — and
// caches it module-wide (the library is static for a deploy, so a re-mount of
// the New-alarm flow never re-hits the network). The metadata (param
// label/unit/bounds/perMode + compileSupport) arrives entirely over the wire, so
// the shapes here are hand-written mirrors read DEFENSIVELY rather than imported
// types. Shapes mirror cloud/src/api/alerts/library.ts (`AlertLibraryEntry` /
// `RecipeParamSpec`).
//
// The one thing NOT mirrored is the storage↔display arithmetic: `toDisplayValue`
// / `fromDisplayValue` come from `@neomaris/rules` itself (already a runtime
// dependency of this bundle — see `alarmDefsForm`'s `CANONICAL_MODES` and
// `AlarmNew`'s `parseRecipeId`). Re-deriving "divide by 1000" here would be a
// second source of truth for a conversion whose whole point is that both ends
// agree to the millisecond.

import type { UnitSettings } from '@neomaris/protocol';
import {
  DEFAULT_ENGINE_RUNNING_RPM,
  fromDisplayValue,
  parseRecipeId,
  toDisplayValue,
  type ParamDisplay,
  type ParamDisplayUnit,
} from '@neomaris/rules';
import { useEffect, useState } from 'react';

import { api } from '../api';
import { unitVocabulary, type AlarmSeverity } from './alarmDisplay';
import { convertQuantity, formatQuantity, type UnitCategory } from './displayUnits';

/** Whether the shared compiler can bind a recipe to a rule today. */
export type CompileSupport = 'compiled' | 'pending';

/** One editable parameter's display metadata (mirrors `RecipeParamSpec`). */
export interface RecipeParamMeta {
  /** Threshold key — mirrors the recipe param key verbatim. */
  readonly key: string;
  readonly label: string;
  /** The STORED unit — `ms`, `ratio`, `V`, … NOT necessarily what an owner reads. */
  readonly unit: string;
  /** Bounds and increment in STORED units, exactly as the API validates them. */
  readonly min?: number;
  readonly max?: number;
  readonly step?: number;
  /** Whether the value may differ per operating mode (else one shared value). */
  readonly perMode: boolean;
  /**
   * Present ⇒ the owner EDITS this param in a friendlier unit than it is stored
   * in ("Wait before setting off: 60 seconds" over a stored `debounceMs: 60000`).
   * Purely presentational — everything on the wire, in the PATCH body and in the
   * definition digest stays in the stored unit. Read it through
   * {@link paramEditorUnits}, never directly: it is wire data.
   */
  readonly display?: ParamDisplay;
}

/**
 * Metadata saying this recipe accepts the `engine_running` condition preset
 * (mirrors `RecipeConditionSupport` in `@neomaris/rules`). Only the two engine
 * recipes carry it today — the API rejects the preset on any recipe without it,
 * so the form offers the toggle exactly where this is present.
 */
export interface RecipeConditionMeta {
  readonly preset: 'engine_running';
  /** The RPM floor the form prefills ("counts as running above <n> RPM"). */
  readonly defaultMinRpm: number;
}

/**
 * The path FAMILY a recipe's compile arm walks (mirrors `RecipePathFamily` in
 * `@neomaris/rules`), as the regex source + flags of the compiler's OWN pattern.
 * Present only on the recipes that pick among sibling instances — a fixed-path
 * recipe (GPS loss, anchor drift, shore power) has one constant path and nothing
 * to choose between.
 *
 * Read it through {@link pathFamilyMatcher}, never with a hand-written regex:
 * this is the one thing that keeps the binding picker from offering a path the
 * compiler would refuse to pin.
 *
 * Carried on the draft (`AlarmDraft.bindingFamily`) exactly like the other
 * template metadata the form needs, so the reducer stays pure and testable.
 */
export interface RecipePathFamilyMeta {
  readonly source: string;
  readonly flags: string;
  /**
   * True when the family has exactly ONE member — a fixed-path recipe such as
   * GPS position lost or anchor drift, which is one-per-boat. Absent for a real
   * family (tanks, batteries, engines), which can hold one alarm per instance.
   * Mirrors `RecipePathFamily.singleMember` in @neomaris/rules.
   */
  readonly singleMember?: true;
}

/** One library entry (mirrors `AlertLibraryEntry`). */
export interface AlertTemplate {
  readonly recipeId: string;
  readonly title: string;
  readonly description: string;
  readonly defaultSeverity: AlarmSeverity;
  readonly defaultModes: string[];
  readonly params: RecipeParamMeta[];
  readonly compileSupport: CompileSupport;
  /** Present ⇒ this recipe takes the engine-running condition preset. */
  readonly supportsCondition?: RecipeConditionMeta;
  /** Present ⇒ this recipe's rule can be PINNED to one path in this family. */
  readonly pathFamily?: RecipePathFamilyMeta;
  /**
   * `true` ⇒ a pin is MANDATORY: this recipe has no family walk, so an alarm
   * saved without a `binding.path` compiles pending forever and the API refuses
   * it (`422 invalid_binding`). Core ADR-116 part A.
   *
   * `bilge_high` is the only one, and the reason is that its family — every
   * switch channel on the boat — contains the cabin lights as readily as the
   * float switch. "Automatic (first reported)" is not a safe answer there, so
   * the form must not offer it.
   *
   * Read through {@link templateRequiresPin}: wire data, and only a literal
   * `true` may change the form. An unreadable value leaves the form exactly as
   * it behaved before this field existed, and the server's refusal is the
   * backstop.
   */
  readonly requiresPin?: boolean;
  /**
   * `true` ⇒ SUPERSEDED. The recipe still compiles and every alarm already on it
   * keeps working and rendering — but it is not offered for a new one, because
   * something better replaced it ("Tank high / low" by the three per-fluid tank
   * alarms; "House battery low/critical" by the per-bank pair).
   *
   * Read through {@link isDeprecatedTemplate}: this is wire data, and only a
   * literal `true` may hide a card.
   */
  readonly deprecated?: boolean;
  /**
   * Present ⇒ this recipe needs boat software at least this new. The compiler is
   * bundled into the boat's own binary, so a recipe minted after a boat's
   * release is one that boat has never heard of — it would store the alarm and
   * watch nothing.
   *
   * This is the LIBRARY's field, not the boat's: the library is fetched once per
   * browser session and shared across every boat in the account. Compare it
   * against the boat on screen with {@link templateNeedsBoatUpdate}.
   */
  readonly minCoreVersion?: string;
}

/**
 * Is this recipe superseded? Strictly `true`, never truthy.
 *
 * DEFENSIVE in the direction that cannot delete a working recipe from the page:
 * a malformed or unreadable value leaves the recipe on offer, which is exactly
 * today's behaviour. The cost of guessing wrong the other way is an owner told
 * their boat cannot have a tank alarm.
 */
export function isDeprecatedTemplate(template: AlertTemplate | null | undefined): boolean {
  return (template as { deprecated?: unknown } | null | undefined)?.deprecated === true;
}

/**
 * Does this recipe REQUIRE a pin? Strictly `true`, never truthy.
 *
 * DEFENSIVE in the direction that cannot block a save the server would accept: a
 * malformed value reads as "optional", so the form behaves as it always has and
 * the API's `422 invalid_binding` is what stops an unpinnable alarm. Guessing
 * the other way would put a required-field error on a recipe that has no such
 * requirement, with no way for the owner to satisfy it.
 */
export function templateRequiresPin(template: AlertTemplate | null | undefined): boolean {
  return (template as { requiresPin?: unknown } | null | undefined)?.requiresPin === true;
}

/**
 * The minimum boat version this recipe needs, or `null` when this boat already
 * has it (or the recipe needs nothing).
 *
 * UNKNOWN OR UNPARSEABLE `boatVersion` ⇒ gated. It mirrors the server's own
 * `meetsCoreVersion` exactly, and for the same reason: the server will refuse
 * the create, so a page that offered the card would walk the owner into a dead
 * form. Erring the other way costs a card that says "needs an update" on a boat
 * that has never told us its version — which is a true statement about what we
 * know.
 */
export function templateNeedsBoatUpdate(
  template: AlertTemplate,
  boatVersion: string | null | undefined
): string | null {
  const raw: unknown = template.minCoreVersion;
  if (typeof raw !== 'string' || raw.length === 0) return null;
  return compareVersions(boatVersion ?? null, raw) >= 0 ? null : raw;
}

const SEMVER_RE = /^(\d+)\.(\d+)\.(\d+)$/;

/** `a` vs `b` as X.Y.Z. An unparseable or absent `a` compares as older. */
function compareVersions(a: string | null, b: string): number {
  const ma = a === null ? null : SEMVER_RE.exec(a);
  const mb = SEMVER_RE.exec(b);
  if (ma === null || mb === null) return -1;
  for (let i = 1; i <= 3; i += 1) {
    const d = Number(ma[i]) - Number(mb[i]);
    if (d !== 0) return d < 0 ? -1 : 1;
  }
  return 0;
}

interface TemplatesResponse {
  readonly recipes: AlertTemplate[];
}

// Module-level cache: one fetch per browser session (the library is static per
// deploy). `inflight` de-dupes concurrent mounts onto a single request.
let cache: AlertTemplate[] | null = null;
let inflight: Promise<AlertTemplate[]> | null = null;

async function loadTemplates(): Promise<AlertTemplate[]> {
  if (cache !== null) return cache;
  if (inflight === null) {
    inflight = api<TemplatesResponse>('/v1/alert-templates')
      .then((r) => {
        cache = Array.isArray(r?.recipes) ? r.recipes : [];
        return cache;
      })
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

export type AlertTemplatesState =
  | { kind: 'loading' }
  | { kind: 'ready'; templates: AlertTemplate[] }
  | { kind: 'error'; message: string };

/** Fetch (once) + expose the alarm recipe library. */
export function useAlertTemplates(): AlertTemplatesState {
  const [state, setState] = useState<AlertTemplatesState>(
    cache !== null ? { kind: 'ready', templates: cache } : { kind: 'loading' }
  );

  useEffect(() => {
    if (cache !== null) {
      setState({ kind: 'ready', templates: cache });
      return;
    }
    let cancelled = false;
    void loadTemplates()
      .then((templates) => {
        if (!cancelled) setState({ kind: 'ready', templates });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message =
          (err as { message?: string })?.message ?? 'Failed to load the alarm library.';
        setState({ kind: 'error', message });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}

/**
 * The library entry for a STORED recipe id, or undefined.
 *
 * PARSES THE BASE FIRST. The library is keyed by catalog recipe
 * (`tank_fuel_low`); a per-instance alarm stores `<base>#<pinnedPath>`
 * (`@neomaris/rules recipe-instance.ts`), and an exact match answers `undefined`
 * for every one of them. That is not a cosmetic miss: `AlarmDetail` computes
 * `canEdit` from `template !== null`, so with no template the Edit control never
 * renders and an owner CANNOT change the threshold on a single tank — on
 * precisely the alarms this whole feature exists to give them. Every other
 * registry lookup in this bundle already parses (`alarmDisplay`, `AlarmNew`);
 * this one did not.
 *
 * The base is also the right answer on the merits: the pin chooses WHICH
 * instance, never what the recipe's params, bounds or units are.
 */
export function findTemplate(
  templates: readonly AlertTemplate[],
  recipeId: string
): AlertTemplate | undefined {
  const { base } = parseRecipeId(recipeId);
  return templates.find((t) => t.recipeId === base);
}

/**
 * The RPM floor the compiler uses when a preset clause omits `minRpm` — the
 * shared engine-running threshold from `@neomaris/rules` (ADR 0220), re-exported
 * rather than mirrored. Only a fallback: the wire normally carries the recipe's
 * own `defaultMinRpm`.
 */
export { DEFAULT_ENGINE_RUNNING_RPM };

/**
 * The condition-preset metadata for a template, read DEFENSIVELY — this is wire
 * data, and a malformed block must degrade to "this recipe takes no preset"
 * rather than render a toggle that writes a body the API rejects. A present
 * block whose `defaultMinRpm` is unusable still enables the toggle, with the
 * mirrored default filled in (the RPM floor is the operator's to set anyway).
 */
export function conditionSupport(
  template: AlertTemplate | null | undefined
): RecipeConditionMeta | undefined {
  const raw: unknown = template?.supportsCondition;
  if (raw === null || typeof raw !== 'object') return undefined;
  const { preset, defaultMinRpm } = raw as { preset?: unknown; defaultMinRpm?: unknown };
  if (preset !== 'engine_running') return undefined;
  const rpm =
    typeof defaultMinRpm === 'number' && Number.isFinite(defaultMinRpm) && defaultMinRpm > 0
      ? defaultMinRpm
      : DEFAULT_ENGINE_RUNNING_RPM;
  return { preset: 'engine_running', defaultMinRpm: rpm };
}

/**
 * Regex flags this build is willing to compile from the wire. The families ship
 * with none today; `i`/`u` are the only ones a path pattern could ever want, and
 * the stateful ones (`g`/`y`) are refused outright — a `lastIndex`-carrying
 * regex reused across a list silently skips entries, which here would mean
 * dropping instances out of the picker.
 */
const ALLOWED_FAMILY_FLAGS = /^[iu]*$/;

/**
 * A predicate matching every path in a recipe's family, or `undefined` when the
 * recipe has no family (nothing to pin, so the editor offers no choice).
 *
 * DEFENSIVE, like {@link conditionSupport}: this is wire data compiled into a
 * `RegExp`, so a malformed source, an odd flag, or a missing block degrades to
 * "this recipe cannot be pinned here" rather than throwing inside a render. An
 * unanchored source is refused too — the compiler's families are all `^…$`, and
 * a partial match would offer paths the compiler rejects, which is the exact
 * mismatch this metadata exists to prevent.
 */
export function pathFamilyMatcher(
  family: RecipePathFamilyMeta | null | undefined
): ((path: string) => boolean) | undefined {
  const raw: unknown = family;
  if (raw === null || raw === undefined || typeof raw !== 'object') return undefined;
  const { source, flags } = raw as { source?: unknown; flags?: unknown };
  if (typeof source !== 'string' || source.length === 0) return undefined;
  if (!source.startsWith('^') || !source.endsWith('$')) return undefined;
  const f = typeof flags === 'string' ? flags : '';
  if (!ALLOWED_FAMILY_FLAGS.test(f)) return undefined;
  let re: RegExp;
  try {
    re = new RegExp(source, f);
  } catch {
    return undefined;
  }
  return (path: string) => re.test(path);
}

// ---------------------------------------------------------------------------
// The unit an owner EDITS in (`RecipeParamMeta.display`).
//
// Storage never moves. `debounceMs` is milliseconds on the wire, in the PATCH
// body and in the definition digest, before this change and after it; a tank
// level is a 0..1 ratio in all three. What moves is the box on screen, which
// used to read "Debounce (0–3600000 ms)" — a name nobody uses for a thing
// nobody counts in.
//
// Everything the editor needs comes out of ONE resolver, {@link
// paramEditorUnits}: the suffix beside the box, the bounds in the hint, the
// spinner step, and the two conversions. One resolver rather than a table per
// surface is the point — a label that agreed with the box but not with the hint
// is exactly the bug this replaces.
// ---------------------------------------------------------------------------

/**
 * What follows the number in the editor, per edited unit. Owner's words.
 *
 * DRIFT WARNING — the API has a twin of this table in
 * `cloud/src/core/neo/alarm-tools.ts`, for the words on Neo's proposal card. It
 * cannot be shared: that runs in the server bundle, and this copy is also what
 * validates a `display.unit` arriving over the wire from a server that may be a
 * newer build (see {@link readParamDisplay}). Confirming a proposal card opens
 * this editor on the same numbers, so the two must agree — change both.
 */
const DISPLAY_UNIT_SUFFIX: Readonly<Record<ParamDisplayUnit, string>> = {
  seconds: 'seconds',
  minutes: 'minutes',
  percent: '%',
};

/**
 * The wire's `display` block, or `undefined` when this param is edited in its
 * stored unit.
 *
 * DEFENSIVE, like {@link conditionSupport} and {@link pathFamilyMatcher}. The
 * unit enum is CLOSED (`seconds` / `minutes` / `percent`): a build that meets a
 * fourth value it has never heard of must fall back to editing the STORED
 * number, because the alternative is applying a conversion it had to guess and
 * writing a threshold off by a factor of a thousand. A non-positive or
 * non-finite `step` is dropped rather than handed to `<input step>`, where it
 * would wedge the spinner.
 */
function readParamDisplay(raw: unknown): ParamDisplay | undefined {
  if (raw === null || typeof raw !== 'object') return undefined;
  const { unit, step } = raw as { unit?: unknown; step?: unknown };
  if (typeof unit !== 'string' || !(unit in DISPLAY_UNIT_SUFFIX)) return undefined;
  const validUnit = unit as ParamDisplayUnit;
  return typeof step === 'number' && Number.isFinite(step) && step > 0
    ? { unit: validUnit, step }
    : { unit: validUnit };
}

// ---------------------------------------------------------------------------
// The ACCOUNT's units, layered over the registry's own display metadata
// (ADR 0189 §4 — the scoped amendment to "VERBATIM STORE UNITS", plan D6).
//
// The block above is the wire's closed `seconds | minutes | percent` enum: the
// registry decides that a millisecond is edited in seconds, and every account
// sees the same box. This block is the other axis — a threshold that IS a
// physical measurement (an engine temperature, a depth, an anchor radius) reads
// and edits in the unit the ACCOUNT chose, exactly as every other readout in the
// app does since ADR 0189.
//
// Three rules hold this to presentation only:
//
//   1. STORAGE AND WIRE NEVER MOVE. Everything the API, the compiler, the boat
//      and `defsDigest` see stays in the boat's declared unit. `ParamDisplayUnit`
//      is untouched and stays closed — nothing here widens it.
//   2. THE STORE-UNIT GATE. A param converts only when its own declared unit is
//      the category's marine-metric store unit, so a param declaring something
//      else passes through rather than being guessed at (the same rule
//      `displayUnits.categoryFor` applies to telemetry).
//   3. AN UNTOUCHED BOX RE-EMITS ITS ORIGINAL NUMBER, BIT FOR BIT. Rounding a
//      converted value to the unit's display precision does NOT round-trip
//      (20 m reads "66 ft", and 66 ft is 20.1168 m). That is deliberate — the
//      alternative is a box reading "65.6167979" — and it is safe ONLY because
//      `alarmDefsForm` captures a baseline at init and re-emits the original
//      stored number for any box whose text the owner did not change. Without
//      that guard this would move `defsDigest` on a save that changed nothing
//      and resync the whole ruleset.
//
// FORWARD conversion is `displayUnits`' — one table for the whole app. The
// INVERSE lives here because nothing else in the app parses a typed measurement
// back into store units, and `displayUnits` therefore exports no such direction.
// `alertTemplates.test.ts` pins each factor against `convertQuantity` so the two
// cannot drift.
// ---------------------------------------------------------------------------

/**
 * The account unit category a recipe param's number belongs to, or `undefined`
 * for a param that is not a measurement of that kind and must never convert.
 *
 * EXPLICIT PER KEY, never inferred from the unit string. `minKpa` is the reason:
 * it is a pressure in every physical sense, and inferring would print an engine
 * oil pressure in inches of mercury — a barometric unit, on an engine gauge,
 * that no marine display uses. The same reasoning keeps `voltsLow`, the counts
 * and every duration out: they have no account category at all, and their boxes
 * stay exactly as they are for every account.
 *
 * Shared with `alarmDisplay`'s threshold formatters (`ThresholdSpec.category`),
 * so the sentence, the detail row and the edit box cannot disagree about what
 * unit a threshold is in.
 */
const PARAM_UNIT_CATEGORY: Readonly<Record<string, UnitCategory>> = {
  // Engine + fridge over-temperature.
  maxCelsius: 'temperature',
  // Shallow water — depth under the keel, which has its own account setting
  // (m / ft / fathoms) separate from short distances. `shallow_depth` and
  // `anchor_depth_low` both spell it `minMeters`.
  minMeters: 'depth',
  // `deep_depth` watches the same sounder path from the other side. It is a
  // depth for the same reason its shallow twin is, and leaving it out would
  // have left an imperial owner typing metres into a field reading feet.
  maxMeters: 'depth',
  // Anchor watch radius and the GPS source disagreement: distances across the
  // water, in the account's short-distance unit.
  radiusMeters: 'shortDistance',
  disagreementMeters: 'shortDistance',
};

/** {@link PARAM_UNIT_CATEGORY} as a lookup. Unmapped ⇒ this param never converts. */
export function alarmParamCategory(paramKey: string): UnitCategory | undefined {
  return PARAM_UNIT_CATEGORY[paramKey];
}

/**
 * The marine-metric unit each convertible category's thresholds are DECLARED in
 * by the registry. A param whose `unit` is anything else fails the gate and is
 * left alone.
 *
 * Only the three categories {@link alarmParamCategory} can return. Pressure is
 * deliberately absent: adding it here is the one edit that would let engine oil
 * pressure convert, and ADR 0189 says it never does.
 */
const CATEGORY_STORE_UNIT: Partial<Record<UnitCategory, string>> = {
  temperature: 'C',
  depth: 'm',
  shortDistance: 'm',
};

/**
 * The registry spells engine temperature `°C`, not `C` (`packages/rules`
 * `recipes.ts`), so the gate folds the glyph form onto the canonical store unit
 * before comparing. Without this the temperature params silently never convert —
 * a failure that looks exactly like "the feature works, metric users are fine".
 */
const PARAM_UNIT_ALIASES: Readonly<Record<string, string>> = { '°C': 'C' };

function canonicalParamUnit(unit: string): string {
  return PARAM_UNIT_ALIASES[unit] ?? unit;
}

/** Store ⇄ account-unit arithmetic for one category and one chosen unit. */
interface UnitConversion {
  readonly toEdited: (stored: number) => number;
  readonly toStored: (edited: number) => number;
}

/**
 * The inverse of `displayUnits`' conversion table, for the units an alarm
 * threshold can be edited in. Entries exist only where the account unit DIFFERS
 * from the store unit — a metric account resolves nothing here and takes the
 * identity path.
 *
 * Each `toStored` is the exact algebraic inverse of the matching `toEdited`,
 * written out rather than derived. Deriving it from two probes of the forward
 * function is what a first draft did and it is wrong for temperature: the affine
 * slope recovered as `f(1) - f(0)` is 1.7999999999999972, and 212 °F then stores
 * 100.00000000000016 °C instead of 100.
 *
 * NO ROUNDING, unlike the duration path above. A duration is snapped to whole
 * milliseconds because a millisecond is the smallest thing the store can mean; a
 * temperature or a depth has no such grain, and snapping one would be this
 * file inventing a precision the boat never declared.
 */
const CATEGORY_CONVERSIONS: {
  readonly [K in 'temperature' | 'depth' | 'shortDistance']: Partial<
    Record<UnitSettings[K], UnitConversion>
  >;
} = {
  temperature: {
    F: { toEdited: (v) => v * 1.8 + 32, toStored: (v) => (v - 32) / 1.8 },
  },
  depth: {
    ft: { toEdited: (v) => v / 0.3048, toStored: (v) => v * 0.3048 },
    ftm: { toEdited: (v) => v / 1.8288, toStored: (v) => v * 1.8288 },
  },
  shortDistance: {
    ft: { toEdited: (v) => v / 0.3048, toStored: (v) => v * 0.3048 },
  },
};

/** A resolved account conversion for one param: everything the editor needs. */
interface AccountUnits {
  readonly category: UnitCategory;
  /** The param's canonical store unit — what `displayUnits` must be handed. */
  readonly storeUnit: string;
  readonly conversion: UnitConversion;
  readonly settings: UnitSettings;
}

/**
 * The account conversion for this param, or `undefined` when its box shows the
 * stored number — which is the answer for a metric account, for every param
 * without a category, and for a param whose declared unit is not the category's
 * store unit.
 */
function accountUnits(
  param: RecipeParamMeta,
  settings: UnitSettings | undefined
): AccountUnits | undefined {
  if (settings === undefined) return undefined;
  const category = alarmParamCategory(param.key);
  if (category === undefined) return undefined;
  const storeUnit = CATEGORY_STORE_UNIT[category];
  if (storeUnit === undefined) return undefined;
  // The store-unit gate. The boat's own declaration wins over the category.
  if (canonicalParamUnit(param.unit) !== storeUnit) return undefined;
  const conversions = CATEGORY_CONVERSIONS[category as keyof typeof CATEGORY_CONVERSIONS] as
    | Partial<Record<string, UnitConversion>>
    | undefined;
  const conversion = conversions?.[settings[category]];
  if (conversion === undefined) return undefined;
  return { category, storeUnit, conversion, settings };
}

/**
 * The label beside the box — taken from `displayUnits`, so the editor says `°F`
 * and `ftm` in exactly the words every read-only surface uses.
 */
function accountUnitLabel(account: AccountUnits): string {
  return convertQuantity(0, account.storeUnit, account.category, account.settings).unit;
}

/**
 * How many decimals `displayUnits` prints for a magnitude in this account's
 * unit, read off its own formatted text rather than restated here (the
 * precision rules — 1 decimal under the keel, 2 for inches of mercury, coarser
 * above ten — live in one table and this is not a second copy of it).
 */
function accountDecimals(account: AccountUnits, stored: number): number {
  const { text } = formatQuantity(stored, account.storeUnit, account.category, account.settings);
  const dot = text.indexOf('.');
  return dot === -1 ? 0 : text.length - dot - 1;
}

/**
 * The FINEST precision this account's unit ever prints for this category — the
 * only precision a `<input type="number">` step may be built from.
 *
 * The box's contents are `paramValueText`, which renders at the VALUE's
 * precision, and every rule in `displayUnits` gets COARSER as the magnitude
 * grows, never finer (`coarserAtTen`; the rest are constant). So a probe at the
 * bottom of the scale — the stored number that reads as `1` in the account's
 * unit — is an upper bound on the decimals any value in the box can carry.
 *
 * Sizing it off the declared RANGE instead is the bug this replaces. Depth's
 * range tops out at 100 m, which reads 328 ft, which is above ten and therefore
 * whole feet — so the box got `step: 1` while a real 2.5 m threshold rendered
 * "8.2". A number input rejects that: `8.2` is not a multiple of `1`, the field
 * goes `:invalid`, and the form will not submit a value it put there itself.
 */
function finestAccountDecimals(account: AccountUnits, conversion: UnitConversion): number {
  return accountDecimals(account, conversion.toStored(1));
}

/**
 * A converted bound, rounded AWAY from the allowed range: the quoted minimum is
 * rounded up and the quoted maximum down, both at the unit's display precision.
 *
 * Every conversion here is monotone increasing, so this guarantees the number in
 * the hint is one the API accepts. Rounding to nearest would not: a 0.5 m floor
 * is 1.6404 ft, the hint would read "at least 1.6 ft", and typing exactly 1.6 ft
 * stores 0.48768 m — rejected by the very message that asked for it.
 */
function boundInAccountUnits(edited: number, decimals: number, away: 'up' | 'down'): number {
  const scale = 10 ** decimals;
  // Kill float noise below the rounding decision first: 200 °C converts to
  // 392.00000000000006 °F, which would otherwise floor to 392.1 rather than 392.
  const scaled = Number((edited * scale).toFixed(9));
  return (away === 'up' ? Math.ceil(scaled) : Math.floor(scaled)) / scale;
}

/** Everything an editor needs to show one param in the owner's unit. */
export interface ParamEditorUnits {
  /** True when the edited unit differs from the stored one. */
  readonly converted: boolean;
  /** The suffix beside the input — `seconds`, `minutes`, `%`, `V`, or `''`. */
  readonly unitSuffix: string;
  /** The lower bound IN EDITED UNITS, when the param declares one. */
  readonly min?: number;
  /** The upper bound IN EDITED UNITS, when the param declares one. */
  readonly max?: number;
  /**
   * The spinner increment IN EDITED UNITS. A converted param with no declared
   * step gets 1 — whole seconds / minutes / percent, which is the granularity
   * the custom builder has always offered — rather than the stored step, which
   * would be 1000 ms showing up as a 1000-second jump.
   */
  readonly step?: number;
  /** The stored number as the owner should read it (exact, per unit). */
  readonly toEdited: (stored: number) => number;
  /** The owner's number as it must be stored (durations round to whole ms). */
  readonly toStored: (edited: number) => number;
}

/**
 * How to render and parse one param's input box.
 *
 * Three answers, in this order:
 *
 *   1. The registry's own `display` block (seconds / minutes / percent) — a
 *      closed wire enum, the same for every account, exact integer arithmetic.
 *   2. Failing that, the ACCOUNT's unit for the param's category, when it
 *      differs from the stored one ({@link accountUnits}, ADR 0189 §4).
 *   3. Failing both, the identity: same unit, same bounds, same step, no
 *      conversion. That is the right answer for every quantity — volts, kPa,
 *      counts — where the stored unit already IS the owner's unit, and it is
 *      what a metric account gets everywhere.
 *
 * The two can never collide: a param with a `display` block is stored in `ms` or
 * `ratio`, and neither is any category's store unit. The order is fixed anyway,
 * so a future param carrying both edits in the registry's unit rather than
 * silently picking one.
 *
 * `settings` is optional so a caller with no account context (and every caller
 * that predates ADR 0189) keeps exactly the behaviour it had.
 */
export function paramEditorUnits(
  param: RecipeParamMeta,
  settings?: UnitSettings
): ParamEditorUnits {
  const display = readParamDisplay(param.display);
  if (display !== undefined) {
    const spec = { display };
    return {
      converted: true,
      unitSuffix: DISPLAY_UNIT_SUFFIX[display.unit],
      ...(param.min !== undefined ? { min: toDisplayValue(spec, param.min) } : {}),
      ...(param.max !== undefined ? { max: toDisplayValue(spec, param.max) } : {}),
      step: display.step ?? 1,
      toEdited: (stored) => toDisplayValue(spec, stored),
      toStored: (edited) => fromDisplayValue(spec, edited),
    };
  }
  const account = accountUnits(param, settings);
  if (account !== undefined) {
    const { conversion } = account;
    // ONE precision for the hint, the spinner and the box — and it has to be the
    // one the BOX can reach, which is the finest the unit prints anywhere on the
    // scale, not the one its top end happens to print. Tenths of a foot across a
    // 0–328 ft depth range (because a 2.5 m threshold reads "8.2"), tenths of a
    // degree across 32–392 °F.
    const decimals = finestAccountDecimals(account, conversion);
    return {
      converted: true,
      unitSuffix: accountUnitLabel(account),
      ...(param.min !== undefined
        ? { min: boundInAccountUnits(conversion.toEdited(param.min), decimals, 'up') }
        : {}),
      ...(param.max !== undefined
        ? { max: boundInAccountUnits(conversion.toEdited(param.max), decimals, 'down') }
        : {}),
      // The stored step is in store units (0.1 m), so it would spin the box by a
      // third of a foot. One display increment is the honest replacement.
      step: 1 / 10 ** decimals,
      toEdited: conversion.toEdited,
      toStored: conversion.toStored,
    };
  }
  return {
    converted: false,
    unitSuffix: param.unit,
    ...(param.min !== undefined ? { min: param.min } : {}),
    ...(param.max !== undefined ? { max: param.max } : {}),
    ...(param.step !== undefined ? { step: param.step } : {}),
    toEdited: (stored) => stored,
    toStored: (edited) => edited,
  };
}

/** The most decimal places {@link paramValueText} will spend looking for a
 *  round-tripping rendering before giving up and printing the raw number. */
const MAX_VALUE_DECIMALS = 12;

/**
 * A stored value as the string to put IN the input box.
 *
 * Not simply `String(toEdited(stored))`. Binary floating point makes some exact
 * conversions print their own noise — a stored ratio of `0.155` converts to
 * `15.500000000000002` percent, and an owner who merely OPENED the form would
 * see that in the box, then save it back as `0.15500000000000003`: a threshold
 * they never touched, changed, recompiled and re-delivered to the boat.
 *
 * So the rendering is chosen, not computed: the shortest decimal that converts
 * back to the value actually stored. `0.155` renders `15.5`, which stores
 * `0.155` again — bit for bit, so the PATCH diff stays empty. Only when no
 * rounding round-trips — a stored number carrying more precision than the unit
 * can express, i.e. a fractional millisecond; see the note on the fallback for
 * the one case that is and what it costs — does it fall back to the raw
 * conversion, which is honest: the box then shows what is really there.
 */
export function paramValueText(
  param: RecipeParamMeta,
  stored: number,
  settings?: UnitSettings
): string {
  if (!Number.isFinite(stored)) return '';
  // ACCOUNT UNITS BYPASS THE SEARCH (ADR 0189 §4). A shortest-round-tripping
  // rendering exists for feet and degrees Fahrenheit too, but it is unreadable:
  // 20 m of depth would fill the box with "65.6167979", because that is the
  // shortest decimal that multiplies back to exactly 20. So a converted
  // measurement is rounded to the unit's own display precision — "66 ft" — and
  // deliberately does NOT round-trip.
  //
  // What makes that safe is the baseline guard in `alarmDefsForm`: the draft
  // remembers this exact text beside the number it came from, and a box the
  // owner did not change re-emits the ORIGINAL stored number with no arithmetic.
  // Rendering and storage are decoupled on purpose; read `storedNumber` there
  // before changing either.
  const account = accountUnits(param, settings);
  if (account !== undefined) {
    const { text } = formatQuantity(stored, account.storeUnit, account.category, account.settings);
    const rounded = Number(text);
    // `toFixed` pads ("86.0"); an input box wants the plain number back.
    return Number.isFinite(rounded) ? String(rounded) : text;
  }
  const units = paramEditorUnits(param, settings);
  const edited = units.toEdited(stored);
  for (let decimals = 0; decimals <= MAX_VALUE_DECIMALS; decimals += 1) {
    const rounded = Number(edited.toFixed(decimals));
    if (units.toStored(rounded) === stored) return String(rounded);
  }
  // Reached only by a FRACTIONAL stored millisecond (`debounceMs: 1500.5` —
  // legal, since `paramsShape` is `z.number()`, not `.int()`, and nothing this
  // product writes produces one). `fromDisplayValue` rounds a duration to a
  // whole millisecond, so no rendering of 1.5005 seconds stores 1500.5 back and
  // the box shows the honest raw conversion instead.
  //
  // This used to cost one spurious PATCH rewriting the value to 1501 on an
  // untouched save. It no longer does: the second of the two fixes weighed here
  // — "carrying the original stored value alongside the draft" — is exactly what
  // `alarmDefsForm`'s baseline now is, so an untouched box re-emits 1500.5
  // verbatim (pinned in `alarmDefsForm.test.ts`).
  return String(edited);
}

/**
 * A bound, or any other number, written the way the editor writes values —
 * used for the hint under a legend ("0–3600 seconds") and for the validation
 * messages, so a rejected value and the limit it broke are quoted in ONE unit.
 */
export function formatEditedNumber(value: number): string {
  if (!Number.isFinite(value)) return '';
  return String(Math.round(value * 1e6) / 1e6);
}

// ---------------------------------------------------------------------------
// STORE domain → the box's domain (ADR 0170).
//
// A threshold box can now show the live reading of the signal the alarm watches
// ("Now 26.4 V" under "Low voltage"). For that to help rather than mislead, the
// reading and the number the owner types must be the SAME quantity in the SAME
// unit — and for two params they are not:
//
//   - `minKpa` is authored in kPa and stored in kPa, but the boat reports oil
//     pressure in hPa: the compiler multiplies by `KPA_TO_HPA` (10) on its way
//     to the rule. A raw "Now 1013 hPa" under a box reading "100" is worse than
//     no hint at all.
//   - `highRatio` / `lowRatio` are stored as 0..1 ratios but EDITED in percent
//     (`display: { unit: 'percent' }`, the compiler's `RATIO_TO_PERCENT`), and
//     percent is also the store domain for a tank level. Editor and reading
//     already agree; the conversion happens on the authored value, not here.
//
// The store-unit factors and glyphs are NOT restated here. They are
// `alarmDisplay.unitVocabulary`'s — the one table the list rows' value line and
// the detail panel's trigger row also read — so a box, a row and a panel cannot
// end up disagreeing about what an hPa sample is worth in kPa. This file maps
// PARAM KEY → that vocabulary, and nothing more.
//
// Everything not in the table below has no live counterpart and gets NO hint:
// the durations, `maxCycles` (a count of starts, not a reading), and
// `disagreementMeters` (a difference between two sources, which no single path
// reports). A param this build has never seen also gets none — an unrecognised
// key is not an invitation to print an unconverted number.
// ---------------------------------------------------------------------------

/**
 * How a live STORE-domain reading maps onto one param's editor box — the two
 * genuinely different ways a box's number can differ from the wire's.
 *
 * A single multiply cannot express both. `hPa → kPa` is a factor; `°C → °F` is
 * AFFINE, and it also carries a precision (one decimal, `displayUnits`') that a
 * factor knows nothing about. So the account conversion is not performed here at
 * all: this says WHICH conversion applies and hands back the two arguments
 * `displayUnits` needs to do it, which is the same pair every read-only surface
 * on the row is already using.
 */
export type ParamLiveDomain =
  | {
      /**
       * A pure store-domain restatement — the pre-ADR-0189 answer, byte for
       * byte, and what a metric account gets everywhere.
       */
      readonly kind: 'rescale';
      /** Multiply a store-domain number by this to get the box's number. */
      readonly factor: number;
      /** The unit to print beside the converted number — always the BOX's unit. */
      readonly unitSuffix: string;
      /**
       * The unit the reading is expected to arrive in. Present only where
       * {@link factor} is not 1: there the conversion is only safe if the boat
       * is really reporting that unit, so a signal declaring something else gets
       * no hint rather than a number scaled by 10.
       */
      readonly storeUnit?: string;
    }
  | {
      /**
       * The ACCOUNT moved this box (ADR 0189), so the hint under it has to make
       * the same move. Hand the reading's OWN declared unit, this `category` and
       * these `settings` to `displayUnits` (through `formatThreshold` /
       * `observationText`) — that runs the store-unit gate, the affine
       * arithmetic and the precision in one place. A declared unit the gate
       * refuses means the reading CANNOT make the move, and the caller must
       * withhold the hint rather than print metres under a box reading feet.
       */
      readonly kind: 'account';
      readonly category: UnitCategory;
      readonly settings: UnitSettings;
      /** The unit to print beside the converted number — always the BOX's unit. */
      readonly unitSuffix: string;
    };

/** The store-domain arm, which is all the table below can hold. */
type StoreRescale = Extract<ParamLiveDomain, { kind: 'rescale' }>;

/**
 * The box's domain for a param whose reading arrives in `storeUnit`, taken from
 * the shared vocabulary. The `storeUnit` guard is attached only where the
 * vocabulary actually rescales: a glyph change is safe whatever the boat
 * declares, a ×10 is not.
 */
function fromStoreUnit(storeUnit: string): StoreRescale {
  const vocab = unitVocabulary(storeUnit, { rescale: true });
  return {
    kind: 'rescale',
    factor: vocab.factor,
    unitSuffix: vocab.unit ?? storeUnit,
    ...(vocab.factor === 1 ? {} : { storeUnit }),
  };
}

const PARAM_LIVE_DOMAIN: Readonly<Record<string, StoreRescale>> = {
  voltsLow: { kind: 'rescale', factor: 1, unitSuffix: 'V' },
  maxCelsius: fromStoreUnit('C'),
  minMeters: { kind: 'rescale', factor: 1, unitSuffix: 'm' },
  radiusMeters: { kind: 'rescale', factor: 1, unitSuffix: 'm' },
  minKpa: fromStoreUnit('hPa'),
  highRatio: { kind: 'rescale', factor: 1, unitSuffix: '%' },
  lowRatio: { kind: 'rescale', factor: 1, unitSuffix: '%' },
};

/**
 * How to restate a live reading of the alarm's own signal in the unit of this
 * param's input box, or `null` when the two are not comparable and no reading
 * should be shown.
 *
 * The ACCOUNT arm is gated on {@link accountUnits} — the very resolver
 * {@link paramEditorUnits} uses to decide what the box shows — so hint and box
 * cannot disagree about whether this param converted. That agreement used to be
 * re-derived at the call site out of `alarmParamCategory` plus a `converted`
 * flag, which is two statements of one fact and only one screen honouring it.
 *
 * `settings` is optional and omitting it is the whole regression guarantee: with
 * no account context the account arm can never be reached, and the answer is the
 * table's, with the suffix taken from {@link paramEditorUnits} asked WITHOUT
 * settings — so a ratio box still says `%` exactly as its own suffix does, and
 * every uncategorized param is byte-identical to before ADR 0189.
 */
export function paramLiveDomain(
  param: RecipeParamMeta,
  settings?: UnitSettings
): ParamLiveDomain | null {
  const mapped = PARAM_LIVE_DOMAIN[param.key];
  // Not in the table ⇒ no live counterpart at all. Asked FIRST, so a param with
  // a category but no reading (`disagreementMeters`) still gets no hint.
  if (mapped === undefined) return null;
  const account = accountUnits(param, settings);
  if (account !== undefined) {
    return {
      kind: 'account',
      category: account.category,
      settings: account.settings,
      unitSuffix: accountUnitLabel(account),
    };
  }
  const editor = paramEditorUnits(param);
  return editor.converted ? { ...mapped, unitSuffix: editor.unitSuffix } : mapped;
}
